import { useEffect, useState } from 'react'
import { useCascadeStore } from '../store'
import { NODE_COLORS } from '../types'

const CHOICE_SPREAD_Y_DEFAULT = 90
const CONNECTOR_LENGTH = 100

export function ChoiceBubbles() {
  const { activeChoice, fadingChoiceIds, pickChoice } = useCascadeStore()
  const [animatedIn, setAnimatedIn] = useState(false)

  // Trigger entrance animation
  useEffect(() => {
    if (activeChoice) {
      // Small delay so the initial render is at opacity-0/translated, then we animate in
      const t = requestAnimationFrame(() => setAnimatedIn(true))
      return () => cancelAnimationFrame(t)
    } else {
      setAnimatedIn(false)
    }
  }, [activeChoice])

  if (!activeChoice) return null

  const { options, baseX, baseY } = activeChoice
  const totalOptions = options.length
  const spreadY = totalOptions > 5 ? 70 : CHOICE_SPREAD_Y_DEFAULT

  return (
    <>
      {options.map((option, idx) => {
        const yOffset = (idx - (totalOptions - 1) / 2) * spreadY
        const x = baseX
        const y = baseY + yOffset

        const isFading = fadingChoiceIds.includes(option.id)
        const colors = NODE_COLORS[option.nodeType] || NODE_COLORS['validation']

        // Dashed connector line from source node to this choice
        const lineStartX = baseX - CONNECTOR_LENGTH
        const lineEndX = baseX
        const lineY = baseY // start from source center
        const lineEndY = y + 30 // center of this choice

        return (
          <g key={option.id}>
            {/* Dashed connector line (SVG, rendered in a separate layer) */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ zIndex: 4 }}
            >
              <path
                d={`M ${lineStartX} ${lineY + 35} C ${lineStartX + CONNECTOR_LENGTH * 0.6} ${lineY + 35}, ${lineEndX - CONNECTOR_LENGTH * 0.4} ${lineEndY}, ${lineEndX} ${lineEndY}`}
                fill="none"
                stroke="var(--border-color)"
                strokeWidth={1.5}
                strokeDasharray="6,4"
                className={`transition-opacity duration-300 ${
                  isFading ? 'opacity-0' : animatedIn ? 'opacity-60' : 'opacity-0'
                }`}
              />
            </svg>

            {/* Choice bubble */}
            <div
              className={`absolute select-none transition-all duration-300 ease-out ${
                isFading
                  ? 'opacity-0 scale-90 pointer-events-none'
                  : animatedIn
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-75 translate-x-[-20px]'
              }`}
              style={{
                left: x,
                top: y,
                zIndex: 30,
                transitionDelay: isFading ? '0ms' : `${idx * 60}ms`,
              }}
            >
              <button
                onClick={() => pickChoice(option)}
                className={`
                  group relative flex items-center gap-3 px-4 py-3
                  rounded-xl border-2 border-dashed shadow-lg
                  min-w-[160px] max-w-[200px]
                  cursor-pointer
                  hover:border-solid hover:shadow-xl hover:scale-105
                  active:scale-100
                  transition-all duration-150
                  ${colors.bg} ${colors.border}
                `}
              >
                {/* Pulsing glow behind on hover */}
                <div className={`absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${colors.bg} blur-sm -z-10`} />

                <span className="text-xl shrink-0">{option.icon}</span>
                <div className="min-w-0 flex-1 text-left">
                  <div className={`text-xs font-bold leading-tight ${colors.text}`}>
                    {option.label}
                  </div>
                  {option.sublabel && (
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5 leading-tight">
                      {option.sublabel}
                    </div>
                  )}
                </div>
              </button>
            </div>
          </g>
        )
      })}
    </>
  )
}
